import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';

import { useIntlCurrency } from 'src/util/i18n/currency';
import { valuePerUnitWithStrong } from 'src/util/i18n/energyPrice';
import { buildDescription } from 'src/util/i18n/timeOfUse';

/**
 * Indicates whether used in a table context (as opposed to a list/card context).
 *
 * In **table contexts**, visual consistency is critical: every row must display a description,
 * even for simple "all times" clauses. Without this, some table cells would be empty while
 * others contain content, creating an inconsistent and confusing user experience.
 *
 * In **list contexts** (like cards or bullet points), this level of consistency is not required.
 * Simple clauses can omit descriptions without creating visual inconsistency.
 */
const USE_IN_TABLE = false;

/**
 * Individual clause item component that displays a single clause with its value and description.
 * Extracted as a separate component to comply with React Rules of Hooks.
 * @param {object} props - Component props
 * @param {object} props.clause - Clause object containing time-of-use configuration and pricing
 * @returns {React.ReactElement} List item with formatted clause information
 */
function ClauseItem({ clause }) {
  const intl = useIntl();
  const intlCurrency = useIntlCurrency();

  const value = valuePerUnitWithStrong(intl, intlCurrency, clause.price);
  const description = buildDescription(intl, clause, USE_IN_TABLE);

  const formattedClause = intl.formatMessage({
    id: 'trade_rule.time_of_use.clause_list_tou.clause_item.format',
    defaultMessage: '{value} {description}',
  }, { value, description });

  return (
    <li className="ms-3 mt-3" style={{ listStyle: 'circle' }}>
      {formattedClause}
    </li>
  );
}

ClauseItem.propTypes = {
  clause: PropTypes.shape({
    id: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    monthsOfYear: PropTypes.arrayOf(PropTypes.string),
    daysOfWeek: PropTypes.arrayOf(PropTypes.string),
    timesOfDay: PropTypes.arrayOf(
      PropTypes.shape({
        start: PropTypes.shape({
          hours: PropTypes.number.isRequired,
          minutes: PropTypes.number,
          seconds: PropTypes.number,
        }).isRequired,
        finish: PropTypes.shape({
          hours: PropTypes.number.isRequired,
          minutes: PropTypes.number,
          seconds: PropTypes.number,
        }).isRequired,
      }),
    ),
    timezone: PropTypes.string,
    ignoreDaylightSavings: PropTypes.bool,
    ignorePublicHolidays: PropTypes.bool,
  }).isRequired,
};

/**
 * Renders time-of-use clauses as list items with circular bullet points.
 * Each clause displays both the value and description.
 * @param {object} props - Component props
 * @param {Array<object>} props.clauseNodes - Array of clause objects containing time-of-use
 * configuration and pricing information.
 * @returns {React.ReactElement|null} Fragment containing list items with formatted clause
 * information, or null if no clauses
 */
function ClauseListTOU({ clauseNodes }) {
  if (!Array.isArray(clauseNodes) || clauseNodes.length === 0) {
    return null;
  }

  return (
    <>
      {clauseNodes.map((clause) => (
        <ClauseItem key={clause.id} clause={clause} />
      ))}
    </>
  );
}

ClauseListTOU.propTypes = {
  clauseNodes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      price: PropTypes.number.isRequired,
      monthsOfYear: PropTypes.arrayOf(PropTypes.string),
      daysOfWeek: PropTypes.arrayOf(PropTypes.string),
      timesOfDay: PropTypes.arrayOf(
        PropTypes.shape({
          start: PropTypes.shape({
            hours: PropTypes.number.isRequired,
            minutes: PropTypes.number,
            seconds: PropTypes.number,
          }).isRequired,
          finish: PropTypes.shape({
            hours: PropTypes.number.isRequired,
            minutes: PropTypes.number,
            seconds: PropTypes.number,
          }).isRequired,
        }),
      ),
      timezone: PropTypes.string,
      ignoreDaylightSavings: PropTypes.bool,
      ignorePublicHolidays: PropTypes.bool,
    }),
  ).isRequired,
};

export default ClauseListTOU;
